/* eslint-disable import/no-cycle */
/**
 * companySwitch.js
 *
 * Lets an internal Corrpro operator (an AIP admin) act on behalf of a single
 * customer company. Once a company is selected the whole web app is scoped to
 * that one company — every landing page filters its data down to it, and the
 * per-page company filters are removed.
 *
 * The selection is remembered in localStorage so it survives reloads, and the
 * app reloads after a change so every page re-reads the new scope cleanly.
 */
import { hasCognitoGroupName } from './index';
import { AWS_GROUP_NAMES } from '../permissions/constants';

const ACTIVE_COMPANY_KEY = 'cg_active_company';

// The company an operator is asked to pick by default when they first sign in.
export const DEFAULT_SWITCH_COMPANY = 'Corrpro Canada';

/**
 * Only AIP admins may operate on behalf of another company. This keeps the
 * feature to internal Corrpro staff (whose accounts are AIP admins) so they can
 * see the application exactly as each customer does.
 */
export const canSwitchCompany = () =>
	Boolean(hasCognitoGroupName(AWS_GROUP_NAMES.AIP_ADMINS));

/** The company the operator is currently acting as, or null. */
export const getActiveCompany = () => {
	if (!canSwitchCompany()) {
		return null;
	}
	try {
		return localStorage.getItem(ACTIVE_COMPANY_KEY) || null;
	} catch {
		return null;
	}
};

export const hasSelectedCompany = () => Boolean(getActiveCompany());

export const setActiveCompany = name => {
	try {
		localStorage.setItem(ACTIVE_COMPANY_KEY, String(name || ''));
	} catch {
		// storage unavailable — the picker will simply ask again next load
	}
};

export const clearActiveCompany = () => {
	try {
		localStorage.removeItem(ACTIVE_COMPANY_KEY);
	} catch {
		// ignore
	}
};

/**
 * True when the operator must pick a company before they can use the app. Only
 * applies to eligible (AIP admin) users who have not yet chosen one.
 */
export const mustSelectCompany = () =>
	canSwitchCompany() && !hasSelectedCompany();

const normalizeCompanyName = name =>
	String(name || '')
		.replace(/^Customer:/i, '')
		.replace(/[\s_]+/g, ' ')
		.trim()
		.toLowerCase();

const COMPANY_ALIASES = {
	cpc: ['cpc midstream'],
	'cpc midstream': ['cpc']
};

const sameCompany = (left, right) => {
	if (left === right) {return true;}
	const aliases = COMPANY_ALIASES[left] || [];
	return aliases.includes(right);
};

/**
 * True when the given customer belongs to the active company. When no company
 * is active (a regular customer user, or an operator who has not switched) every
 * record matches, so pages behave exactly as before.
 */
export const matchesActiveCompany = customerName => {
	const active = getActiveCompany();
	if (!active) {
		return true;
	}
	return sameCompany(normalizeCompanyName(customerName), normalizeCompanyName(active));
};

export { normalizeCompanyName };
